import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Chip,
    CircularProgress,
    TextField,
    Alert,
    Stepper,
    Step,
    StepLabel,
} from "@mui/material"
import {
    ArrowForward as ArrowIcon,
    Warning as WarningIcon,
    CheckCircle as CheckIcon,
    CalendarToday as CalendarIcon
} from "@mui/icons-material"
import React, {useEffect, useState} from "react"
import { useTranslation } from "react-i18next"
import { ORDER_STATUS_FLOW } from "../../../components/orders/order-status-validator"

const OrderStatusChangeDialog = ({ open, onClose, order, newStatus, orderStatuses, onConfirm, loading, error, uncertain, timeZone }) => {
    const { t } = useTranslation()
    const [reason, setReason] = useState("")
    const [validationError, setValidationError] = useState("")
    const [deliveryDate, setDeliveryDate] = useState("")

    const currentStatus = order?.status?.toLowerCase()
    const targetStatus = newStatus?.toLowerCase()

    // Validate the status change
    const validation = ORDER_STATUS_FLOW.validateStatusChange(currentStatus, targetStatus, reason)

    const handleConfirm = () => {
        if (!validation.isValid) {
            console.log(validation.message)
            setValidationError(t(validation.message))
            return
        }

        onConfirm(reason.trim() || null, order.orderNumber, targetStatus, deliveryDate || null)
    }

    const getMinDate = () => {
        if (!timeZone) { return ""; }
        try {
            const parts = Object.fromEntries(new Intl.DateTimeFormat("en", {timeZone, year: "numeric", month: "2-digit", day: "2-digit"})
                .formatToParts(new Date()).map(part => [part.type, part.value]));
            return `${parts.year}-${parts.month}-${parts.day}`;
        } catch { return ""; }
    }

    useEffect(() => {
        setDeliveryDate("");
    }, [open])

    const handleClose = () => {
        if (loading) { return; }
        onClose()
        setReason("")
        setValidationError("")
    }

    const StatusChip = ({ status, label }) => {
        const statusConfig = orderStatuses[status]
        if (!statusConfig) return null

        return (
            <Chip
                label={t(label || statusConfig.label)}
                color={statusConfig.color}
                size="small"
                icon={statusConfig.icon}
                sx={{ fontWeight: "medium" }}
            />
        )
    }

    const getSteps = () => {
        const steps = []
        const currentStatusConfig = orderStatuses[currentStatus]
        const targetStatusConfig = orderStatuses[targetStatus]

        if (currentStatusConfig) steps.push(currentStatusConfig.label)
        if (targetStatusConfig) steps.push(targetStatusConfig.label)

        return steps
    }

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <ArrowIcon color="primary" />
                    {t("Confirm")}
                </Box>
            </DialogTitle>

            <DialogContent>
                {error && <Alert severity="error" sx={{mb: 2}}>{t(error)}</Alert>}
                {/* Order Info */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="body1" gutterBottom>
                        {t("ChangeStatusForOrder")} <strong>{order?.orderNumber}</strong>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t("Customer")}: {order?.customerName}
                    </Typography>
                </Box>

                {/* Status Transition Visualization */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" gutterBottom>
                        {t("StatusTransition")}:
                    </Typography>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            p: 2,
                            bgcolor: "grey.50",
                            borderRadius: 1,
                        }}
                    >
                        <StatusChip status={currentStatus} />
                        <ArrowIcon color="action" />
                        <StatusChip status={targetStatus} />
                    </Box>
                </Box>

                {/* Progress Stepper */}
                <Box sx={{ mb: 3 }}>
                    <Stepper activeStep={1} alternativeLabel>
                        {getSteps().map((label, index) => (
                            <Step key={label}>
                                <StepLabel>{t(label)}</StepLabel>
                            </Step>
                        ))}
                    </Stepper>
                </Box>

                {/* Validation Errors */}
                {!validation.isValid && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {t(validation.message)}
                    </Alert>
                )}

                {targetStatus === "processing" &&
                    <Box sx={{ mb: 2 }}>
                        <TextField
                            fullWidth
                            type="date"
                            label={t("DeliveryDate")}
                            value={deliveryDate}
                            onChange={(e) => setDeliveryDate(e.target.value)}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            inputProps={{
                                min: getMinDate(),
                            }}
                            InputProps={{
                                startAdornment: <CalendarIcon sx={{ mr: 1, color: "text.secondary" }} />,
                            }}
                            helperText={timeZone ? t("OrderQueueLegacyDelivery", {zone: timeZone}) : t("SelectDeliveryDate")}
                            required
                            disabled={loading || uncertain}
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    "&:hover fieldset": {
                                        borderColor: "primary.main",
                                    },
                                },
                            }}
                        />
                    </Box>
                }

                {/* Reason Input for Cancellation */}
                {validation.requiresReason && (
                    <Box sx={{ mb: 2 }}>
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            label={t("ReasonForCancellation")}
                            placeholder={t("CancellationReasonPlaceholder")}
                            value={reason}
                            disabled={loading || uncertain}
                            inputProps={{maxLength: 255}}
                            onChange={(e) => {
                                setReason(e.target.value)
                                setValidationError("")
                            }}
                            error={!!validationError}
                            helperText={validationError || t("ReasonCharacters")}
                            required
                        />
                    </Box>
                )}

                {/* Business Rule Info */}
                <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2">{t(ORDER_STATUS_FLOW.getStatusDescription(targetStatus))}</Typography>
                </Alert>

                {/* Warning for Final States */}
                {ORDER_STATUS_FLOW.isFinalStatus(targetStatus) && (
                    <Alert severity="warning" icon={<WarningIcon />}>
                        <Typography variant="body2">
                            {t("OrderCancelledDesc")}
                        </Typography>
                    </Alert>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 3 }}>
                <Button onClick={handleClose} disabled={loading}>
                    {t("Cancel")}
                </Button>
                <Button
                    onClick={handleConfirm}
                    variant="contained"
                    disabled={loading || !validation.isValid || (targetStatus === "processing" && !deliveryDate)}
                    startIcon={loading ? <CircularProgress size={16} /> : <CheckIcon />}
                >
                    {loading ? t("Updating") : t(uncertain ? "Retry" : "ConfirmChange")}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default OrderStatusChangeDialog
